import { valueAt } from "../common/valueAt";
import type { AlgorithmOperation, OperationType } from "@algoquest/schemas";

/**
 * Linear Search — framework-independent Algorithm Engine (Master Spec §8,
 * §13, §22). Imports nothing from three/react/DOM; every operation it
 * emits comes from the shared vocabulary in @algoquest/schemas (§15), so
 * the trace, the box visuals, and future analytics all agree on what
 * happened. Raw renderer/input events never become algorithm events.
 *
 * One step() call = one physical "scan this box" action by the player.
 * The engine enforces sequential order itself via `nextIndex`, so it is
 * the single source of truth GameRuntime consults before allowing a scan.
 */
export interface LinearSearchDomainState {
  values: number[];
  target: number;
  nextIndex: number;
  checkedIndices: number[];
  foundIndex: number | null;
  comparisons: number;
  isComplete: boolean;
}

export interface LinearSearchStepResult {
  operations: AlgorithmOperation[];
  state: LinearSearchDomainState;
}

export class LinearSearchEngine {
  private readonly values: number[];
  private readonly target: number;
  private nextIndex = 0;
  private checkedIndices: number[] = [];
  private foundIndex: number | null = null;
  private comparisons = 0;
  private isComplete = false;
  private sequenceCounter = 0;
  private readonly trace: AlgorithmOperation[] = [];

  constructor(values: number[], target: number) {
    if (values.length === 0) {
      throw new Error("LinearSearchEngine requires a non-empty dataset");
    }
    this.values = values;
    this.target = target;
  }

  getState(): LinearSearchDomainState {
    return {
      values: this.values,
      target: this.target,
      nextIndex: this.nextIndex,
      checkedIndices: [...this.checkedIndices],
      foundIndex: this.foundIndex,
      comparisons: this.comparisons,
      isComplete: this.isComplete,
    };
  }

  getTrace(): AlgorithmOperation[] {
    return [...this.trace];
  }

  /**
   * Advances the search by exactly one comparison. Returns null once the
   * search has already concluded (idempotent — safe to call defensively).
   */
  step(nowMs: number): LinearSearchStepResult | null {
    if (this.isComplete) {
      return null;
    }

    if (this.nextIndex >= this.values.length) {
      this.isComplete = true;
      return { operations: [this.emit("NOT_FOUND", nowMs, {})], state: this.getState() };
    }

    const index = this.nextIndex;
    const value = valueAt(this.values, index);
    this.nextIndex += 1;
    this.comparisons += 1;
    this.checkedIndices.push(index);

    const operations: AlgorithmOperation[] = [
      this.emit("SCAN", nowMs, { indices: [index], values: [value] }),
    ];

    if (value === this.target) {
      this.foundIndex = index;
      this.isComplete = true;
      operations.push(this.emit("FOUND", nowMs, { indices: [index], values: [value] }));
      return { operations, state: this.getState() };
    }

    operations.push(this.emit("COMPARE", nowMs, { indices: [index], values: [value] }));

    if (this.nextIndex >= this.values.length) {
      this.isComplete = true;
      operations.push(this.emit("NOT_FOUND", nowMs, {}));
    }

    return { operations, state: this.getState() };
  }

  private emit(
    type: OperationType,
    timestampMs: number,
    partial: Partial<Omit<AlgorithmOperation, "sequence" | "type" | "timestampMs">>,
  ): AlgorithmOperation {
    const operation: AlgorithmOperation = {
      sequence: this.sequenceCounter,
      type,
      timestampMs,
      ...partial,
    };
    this.sequenceCounter += 1;
    this.trace.push(operation);
    return operation;
  }
}
